import { shallowEqual, useSelector } from 'react-redux';
import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import ShakaPlayer from '@/components/app/ShakaAudioPlayer';
import AudioContext from '@/contexts/audio-playback/audio';
import useAudioCompatibility from '@/hooks/audio/useAudioCompatibility';
import useAudioElement from '@/hooks/audio/useAudioElement';
import { isBrowser } from '@/helpers/browser';
import { selectCurrentEpisodeKey, selectCurrentManifestUrl } from '@/state/selectors/audio';

const selector = (state) => ({
  currentlyPlayingEpisodeKey: selectCurrentEpisodeKey(state),
  currentManifestUrl: selectCurrentManifestUrl(state)
});

const AudioPlayer = ({ readyToPlay }) => {
  const { audioRef, audioRefReady } = useAudioElement();
  const {
    onPlayerError,
    onPlayerLoading,
    onPlayerLoaded,
    onPlayerStateChange,
    onPlayerStateIdle,
    onPlayerStreaming,
    onPlayerBuffering
  } = useContext(AudioContext);
  const { currentlyPlayingEpisodeKey, currentManifestUrl } = useSelector(selector, shallowEqual);
  const { shakaSupported, shouldUseNativeHls } = useAudioCompatibility();

  useEffect(() => {
    if (shouldUseNativeHls && audioRef && currentManifestUrl) {
      audioRef.current.src = currentManifestUrl;
    }
  }, [shouldUseNativeHls, currentManifestUrl, audioRef]);

  // Only render shaka player if it is supported by the browser
  if (isBrowser() && shakaSupported && readyToPlay) {
    return (
      <ShakaPlayer
        audioRef={ audioRef }
        audioRefReady={ audioRefReady }
        onPlayerBuffering={ onPlayerBuffering }
        onPlayerError={ onPlayerError }
        onPlayerLoaded={ onPlayerLoaded }
        onPlayerLoading={ onPlayerLoading }
        onPlayerStateChange={ onPlayerStateChange }
        onPlayerStateIdle={ onPlayerStateIdle }
        onPlayerStreaming={ onPlayerStreaming }
        trackId={ currentlyPlayingEpisodeKey }
        url={ currentManifestUrl }
      />
    );
  }

  return null;
};

AudioPlayer.propTypes = { readyToPlay: PropTypes.bool };

AudioPlayer.defaultProps = { readyToPlay: false };

export default AudioPlayer;